import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import MenuIcon from '@material-ui/icons/Menu';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ModuleManagerGrid from 'components/assumptions/ModuleManagerGrid';
import ModuleCard from 'components/assumptions/ModuleCard';
import Module from 'components/assumptions/Module';
import EditDescriptionModal from 'components/assumptions/EditDescriptionModal';
import {
  fetchAssumptionModules,
  setSelectedModuleId,
  setSelectedModuleIsOpen,
  updateAssumptionModule
} from 'store/actions';
import { Constants } from 'utilities/constants';
import { LoadingOverlayWrapper } from 'components/layout';
import { Resizable } from 're-resizable';
import Group from './Group';

const AssumptionsManager = ({
  classes,
  gridRef,
  user,
  modules,
  fetchAssumptionModules,
  modulesAreLoading,
  selectedModule,
  selectedModuleId,
  selectedModuleIsOpen,
  setSelectedModuleIsOpen,
  setSelectedModuleId,
  hasSelectedGroup,
  selectedGroupIsOpen,
  updateAssumptionModule
}) => {
  // == CONSTANTS ===============================================================================

  const canSave = user.roles.administrator;
  const foundModule = modules?.find((module) => module.id === selectedModuleId);

  // == USE STATE ===============================================================================

  const [moduleManagerGridState, setModuleManagerGridState] = useState({
    open: false,
  });

  const [editDescriptionModalIsOpen, setEditDescriptionModalIsOpen] = useState(false);
  const [moduleToEdit, setModuleToEdit] = useState(null);

  // == USE EFFECTS =============================================================================

  useEffect(() => {
    fetchAssumptionModules();
  }, []);

  // == EVENT HANDLERS ==========================================================================

  const handleModuleCardClick = (id) => {
    if (id !== selectedModuleId) {
      setSelectedModuleId(id);
      setSelectedModuleIsOpen(true);
    } else {
      setSelectedModuleIsOpen(true);
    }
  };

  const handleEditDescription = (module) => {
    setModuleToEdit(module);
    setEditDescriptionModalIsOpen(true);
  }

  const handleCloseEditDescriptionModal = () => {
    setEditDescriptionModalIsOpen(false);
  }

  const handleSaveDescription = (description) => {
    handleCloseEditDescriptionModal();
    const module = moduleToEdit.moduleId === selectedModuleId ? selectedModule : moduleToEdit; // If the selected module is being edited, it will be updated with the payload used here. Use selectedModule object because it contains the 'groups' child 
    updateAssumptionModule({ ...module, description });
  }

  // == JSX COMPONENT ===========================================================================

  return (
    <Resizable
      className={classes.resizableContainer}
      minWidth='250' // Use vh/vw or px only, not %. Limitation of re-resizable- % of parent does not calculate correctly in a vertical flex layout
      maxWidth='50vw'
      defaultSize={{ width: '440px' }}
      enable={{
        top: false,
        right: true,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
    >
      <Paper className={classes.panelContainer}>
        {hasSelectedGroup && selectedGroupIsOpen ? (
          <Group canSave={canSave} gridRef={gridRef} />
        ) : selectedModuleId != null && selectedModuleIsOpen ? (
          <Module
            name={selectedModule?.name ?? foundModule?.name ?? null}
            canSave={canSave}
          />
        ) : (
          <>
            <div className={classes.header}>
              <div className={classes.headerLeft}>
                <Tooltip title='Manage Modules'>
                  <IconButton
                    onClick={() => setModuleManagerGridState({ open: true })}
                    size='small'
                    color='inherit'
                    classes={{
                      root: classes.iconButton,
                      disabled: classes.headerIconDisabled,
                    }}
                    style={{ margin: '0 4px' }}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant='subtitle2'
                  className={classes.headerText}
                  color='inherit'
                >
                  {'Modules'}
                </Typography>
              </div>
              <div className={classes.headerRight}>
                <Tooltip title='Create New Module'>
                  <span>
                    <IconButton
                      onClick={() =>
                        setModuleManagerGridState({
                          open: true,
                          shouldCollapseGrid: true,
                        })
                      }
                      disabled={modulesAreLoading}
                      size='small'
                      color='inherit'
                      classes={{
                        root: classes.iconButton,
                        disabled: classes.headerIconDisabled,
                      }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Refresh Modules'>
                  <IconButton
                    onClick={fetchAssumptionModules}
                    disabled={modulesAreLoading}
                    size='small'
                    color='inherit'
                    classes={{
                      root: classes.iconButton,
                      disabled: classes.headerIconDisabled,
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className={classes.listContainer}>
              <LoadingOverlayWrapper
                className={classes.overlay}
                active={modulesAreLoading}
                message='Loading modules...'
              >
                {modules?.length ? (
                  <>
                    {modules.map((module) => (
                      <ModuleCard
                        key={module.moduleId}
                        module={module}
                        onClick={handleModuleCardClick}
                        onEditDescription={handleEditDescription}
                      />
                    ))}
                  </>
                ) : (
                  <div className={classes.noObjectsDiv}>
                    <Typography
                      className={classes.noObjectsText}
                      variant='body2'
                    >
                      No modules found
                    </Typography>
                  </div>
                )}
              </LoadingOverlayWrapper>
            </div>
          </>
        )}
      </Paper>
      <ModuleManagerGrid
        modalOpen={moduleManagerGridState?.open}
        shouldCollapseGrid={moduleManagerGridState?.shouldCollapseGrid}
        onClose={() => setModuleManagerGridState({ open: false })}
      />
      <EditDescriptionModal
        isModalOpen={editDescriptionModalIsOpen}
        onUpdate={handleSaveDescription}
        onCancel={handleCloseEditDescriptionModal}
        value={moduleToEdit?.description ?? ''}
      />
    </Resizable>
  );
};

const styles = () => ({
  resizableContainer: {
    zIndex: 1,
  },
  panelContainer: {
    flexShrink: 0,
    backgroundColor: '#f2f2f2',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#3e4350',
    color: '#fff',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    margin: '8px 0',
  },
  headerText: {
    fontSize: '1.2rem',
    marginRight: 8,
  },
  headerRight: {
    marginRight: 4,
    display: 'flex',
  },
  iconButton: {
    padding: '5px',
  },
  headerIconDisabled: {
    color: 'rgba(255, 255, 255, 0.2)!important',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto',
    paddingTop: 8,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  noObjectsDiv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 42,
    margin: '4px 8px',
  },
  noObjectsText: {
    fontStyle: 'italic',
    color: '#a7a9ac',
    fontSize: '.9rem',
  },
  overlay: {
    height: '100%',
  },
});

const mapStateToProps = ({
  assumptionModulesState,
  assumptionGroupsState,
  appState,
}) => {
  const { selectedModule } = assumptionModulesState;
  const { selectedGroupId } = assumptionGroupsState;

  return {
    user: appState.user,
    modules: assumptionModulesState.modules,
    selectedModule: assumptionModulesState.selectedModule,
    selectedModuleId: assumptionModulesState.selectedModuleId,
    modulesAreLoading: assumptionModulesState.modulesAreLoading,
    selectedModuleIsOpen: assumptionModulesState.selectedModuleIsOpen,
    selectedGroupIsOpen: assumptionGroupsState.selectedGroupIsOpen,
    hasSelectedGroup:
      selectedModule &&
      selectedGroupId !== null &&
      selectedModule.groups &&
      selectedModule.groups.find(
        (group) => group.groupId === selectedGroupId
      ) != null,
  };
};

const mapDispatchToProps = {
  fetchAssumptionModules,
  setSelectedModuleId,
  setSelectedModuleIsOpen,
  updateAssumptionModule
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(AssumptionsManager);
